import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Subtitles, Upload, Wand2, Download, ChevronRight, Eye, EyeOff } from 'lucide-react';

const Landing = () => {
  const navigate = useNavigate();
  const { login, register, isAuthenticated } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });

  if (isAuthenticated) {
    navigate('/dashboard');
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = isLogin 
      ? await login(formData.email, formData.password)
      : await register(formData.email, formData.password, formData.name);

    setLoading(false);

    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
  };

  const features = [
    {
      icon: <Upload className="w-6 h-6" />,
      title: "Upload Fácil",
      description: "Arraste e solte seu vídeo em MP4, MOV, AVI ou WebM"
    },
    {
      icon: <Wand2 className="w-6 h-6" />,
      title: "IA Whisper",
      description: "Detecção automática de idioma e transcrição precisa"
    },
    {
      icon: <Subtitles className="w-6 h-6" />,
      title: "Personalização Total",
      description: "Posição, fonte, tamanho e caixa de fundo customizáveis"
    },
    {
      icon: <Download className="w-6 h-6" />,
      title: "Exportação Flexível",
      description: "Baixe .SRT ou vídeo com legendas embutidas"
    }
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex">
      {/* Left side - Hero */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1770745560263-a8fc696de90b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhcmslMjB0ZXh0dXJlJTIwbm9pc2UlMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc3NTUwODAwNHww&ixlib=rb-4.1.0&q=85)',
            filter: 'brightness(0.3)'
          }}
        />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div>
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-3 mb-16"
            >
              <div className="w-10 h-10 bg-[#FF3B30] rounded-sm flex items-center justify-center">
                <Subtitles className="w-6 h-6 text-white" />
              </div>
              <span className="font-['Outfit'] text-2xl font-bold text-white">Legenda AI</span>
            </motion.div>
            
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="font-['Outfit'] text-5xl lg:text-6xl font-black text-white tracking-tighter mb-6"
            >
              Legendas<br />
              <span className="text-[#FF3B30]">Automáticas</span><br />
              com IA
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="font-['IBM_Plex_Sans'] text-lg text-neutral-400 max-w-md"
            >
              Transcreva e adicione legendas aos seus vídeos em segundos 
              usando a tecnologia OpenAI Whisper.
            </motion.p>
          </div>

          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="grid grid-cols-2 gap-4"
          >
            {features.map((feature, index) => (
              <div 
                key={index}
                className="p-4 bg-white/5 backdrop-blur-sm border border-white/10 rounded-sm"
              >
                <div className="text-[#FF3B30] mb-2">{feature.icon}</div>
                <h3 className="font-['Outfit'] font-bold text-white mb-1">{feature.title}</h3>
                <p className="font-['IBM_Plex_Sans'] text-sm text-neutral-400">{feature.description}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Right side - Auth Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-md"
        >
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <div className="w-10 h-10 bg-[#FF3B30] rounded-sm flex items-center justify-center">
              <Subtitles className="w-6 h-6 text-white" />
            </div>
            <span className="font-['Outfit'] text-2xl font-bold text-white">Legenda AI</span>
          </div>

          <div className="bg-[#141414] border border-white/10 rounded-sm p-8">
            <h2 className="font-['Outfit'] text-2xl font-bold text-white mb-2">
              {isLogin ? 'Bem-vindo de volta' : 'Criar conta'}
            </h2>
            <p className="font-['IBM_Plex_Sans'] text-neutral-400 mb-6">
              {isLogin 
                ? 'Entre na sua conta para continuar' 
                : 'Crie sua conta gratuita para começar'}
            </p>

            {error && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-sm text-red-400 text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div>
                  <Label htmlFor="name" className="text-neutral-300 text-sm font-medium">
                    Nome
                  </Label>
                  <Input
                    id="name"
                    data-testid="register-name-input"
                    type="text"
                    placeholder="Seu nome"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-1 bg-[#1E1E1E] border-white/10 text-white placeholder:text-neutral-500 focus:ring-[#FF3B30] focus:border-[#FF3B30]"
                    required={!isLogin}
                  />
                </div>
              )}

              <div>
                <Label htmlFor="email" className="text-neutral-300 text-sm font-medium">
                  Email
                </Label>
                <Input
                  id="email"
                  data-testid="auth-email-input"
                  type="email"
                  placeholder="seu@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="mt-1 bg-[#1E1E1E] border-white/10 text-white placeholder:text-neutral-500 focus:ring-[#FF3B30] focus:border-[#FF3B30]"
                  required
                />
              </div>

              <div>
                <Label htmlFor="password" className="text-neutral-300 text-sm font-medium">
                  Senha
                </Label>
                <div className="relative mt-1">
                  <Input
                    id="password"
                    data-testid="auth-password-input"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="bg-[#1E1E1E] border-white/10 text-white placeholder:text-neutral-500 focus:ring-[#FF3B30] focus:border-[#FF3B30] pr-10"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button 
                type="submit" 
                data-testid={isLogin ? "login-submit-btn" : "register-submit-btn"}
                disabled={loading}
                className="w-full bg-[#FF3B30] hover:bg-[#D63026] text-white font-semibold py-5 rounded-sm"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Processando...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    {isLogin ? 'Entrar' : 'Criar conta'}
                    <ChevronRight className="w-4 h-4" />
                  </span>
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <button
                type="button"
                data-testid="toggle-auth-mode-btn"
                onClick={() => {
                  setIsLogin(!isLogin);
                  setError('');
                }}
                className="text-neutral-400 hover:text-white text-sm transition-colors"
              >
                {isLogin ? 'Não tem conta? ' : 'Já tem conta? '}
                <span className="text-[#FF3B30] font-medium">
                  {isLogin ? 'Criar conta' : 'Entrar'}
                </span>
              </button>
            </div>
          </div>

          {/* Tier info */}
          <div className="mt-6 grid grid-cols-2 gap-4">
            <div className="p-4 bg-[#141414] border border-white/10 rounded-sm">
              <div className="text-xs font-bold text-neutral-400 uppercase tracking-wider mb-2">Grátis</div>
              <ul className="space-y-1 text-sm text-neutral-300">
                <li>• Vídeos até 1 minuto</li>
                <li>• Download .SRT</li>
                <li>• Detecção de idioma</li>
              </ul>
            </div>
            <div className="p-4 bg-gradient-to-br from-[#FF3B30]/10 to-transparent border border-[#FF3B30]/20 rounded-sm">
              <div className="text-xs font-bold text-[#FF3B30] uppercase tracking-wider mb-2">Pro</div>
              <ul className="space-y-1 text-sm text-neutral-300">
                <li>• Vídeos ilimitados</li>
                <li>• Download .SRT + vídeo</li>
                <li>• Legendas embutidas</li>
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Landing;
